/**
 * THE ONE QUERY-KEY REGISTRY.
 *
 * Every key is `['ll', tenantKey, <domain>, …]` — the tenant key is the API's own rule and is in
 * every key. The signed-in account is in NO key: identity changes clear the cache outright
 * (`clearOnIdentityChange`), they do not re-scope it.
 *
 * The only exception is `bootstrap()`, the one key built before the tenant key is known:
 * `['ll', '_boot']`.
 *
 * Every domain is declared here now, including surfaces nothing reads yet.
 */
import type { QueryClient } from "@tanstack/react-query";

import { subscribeSession } from "./session";

/** Anything a route accepts as a query/path parameter bag. */
export type KeyParams = Record<string, unknown> | undefined;

const p = (params: KeyParams) => (params === undefined ? ([] as const) : ([params] as const));

/**
 * THE PLANNING SCOPE IN A KEY (PS-D12). A scope-keyed read puts the RESOLVED scope id in its key,
 * so two scopes never share a cache entry. This is the key, not the request: the request carries
 * `planningScopeId` only for a non-default selection (`usePlanningScope().requestParams`), while
 * the key always names the scope the answer belongs to. `null` is a bootstrap with no scope list
 * (an older server), which keys as `"_"`.
 */
export const scopedParams = (
  params: Record<string, unknown> | undefined,
  scopeId: string | null,
): Record<string, unknown> => ({ ...params, scopeId: scopeId ?? "_" });

export const BOOTSTRAP_KEY = ["ll", "_boot"] as const;

export const queryKeys = {
  /** Built before the tenant key exists. */
  bootstrap: () => BOOTSTRAP_KEY,

  sites: {
    all: (tenantKey: string) => ["ll", tenantKey, "sites"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "sites", "list", ...p(params)] as const,
    detail: (tenantKey: string, siteId: string) =>
      ["ll", tenantKey, "sites", "detail", siteId] as const,
    /** The site-workspace hub's typeahead, keyed on the TRIMMED term. */
    search: (tenantKey: string, term: string) =>
      ["ll", tenantKey, "sites", "search", term] as const,
  },
  products: {
    all: (tenantKey: string) => ["ll", tenantKey, "products"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "products", "list", ...p(params)] as const,
    detail: (tenantKey: string, productId: string) =>
      ["ll", tenantKey, "products", "detail", productId] as const,
  },
  modes: {
    all: (tenantKey: string) => ["ll", tenantKey, "modes"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "modes", "list", ...p(params)] as const,
    detail: (tenantKey: string, modeId: string) =>
      ["ll", tenantKey, "modes", "detail", modeId] as const,
  },
  carriers: {
    all: (tenantKey: string) => ["ll", tenantKey, "carriers"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "carriers", "list", ...p(params)] as const,
    detail: (tenantKey: string, carrierId: string) =>
      ["ll", tenantKey, "carriers", "detail", carrierId] as const,
  },
  laneCapacities: {
    all: (tenantKey: string) => ["ll", tenantKey, "laneCapacities"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "laneCapacities", "list", ...p(params)] as const,
    detail: (tenantKey: string, laneCapacityId: string) =>
      ["ll", tenantKey, "laneCapacities", "detail", laneCapacityId] as const,
  },
  /**
   * One lane capacity's dated rate history (S1-D23). A rate write invalidates this domain AND
   * `laneCapacities`, whose rows carry the version in force today (`currentRate`).
   */
  laneRates: {
    all: (tenantKey: string) => ["ll", tenantKey, "laneRates"] as const,
    history: (tenantKey: string, laneCapacityId: string) =>
      ["ll", tenantKey, "laneRates", "history", laneCapacityId] as const,
  },
  /** The tenant's exchange rates (AN-D17); tenant-wide, so never scope-keyed. */
  fxRates: {
    all: (tenantKey: string) => ["ll", tenantKey, "fxRates"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "fxRates", "list", ...p(params)] as const,
  },
  calendars: {
    all: (tenantKey: string) => ["ll", tenantKey, "calendars"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "calendars", "list", ...p(params)] as const,
  },
  priorityPolicies: {
    all: (tenantKey: string) => ["ll", tenantKey, "priorityPolicies"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "priorityPolicies", "list", ...p(params)] as const,
    detail: (tenantKey: string, policyId: string) =>
      ["ll", tenantKey, "priorityPolicies", "detail", policyId] as const,
  },
  /** One scope's configuration; the scope id sits under the family prefix so a write clears all. */
  solverConfiguration: {
    all: (tenantKey: string) => ["ll", tenantKey, "solverConfiguration"] as const,
    scope: (tenantKey: string, scopeId: string | null) =>
      ["ll", tenantKey, "solverConfiguration", scopeId ?? "_"] as const,
  },
  /** The tenant's planning scopes, as the admin list reads them (the shell reads the bootstrap). */
  planningScopes: {
    all: (tenantKey: string) => ["ll", tenantKey, "planningScopes"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "planningScopes", "list", ...p(params)] as const,
  },
  /**
   * The tenant's members and open invitations, and the roles an invitation can grant (AU-D12).
   * Tenant-wide, never scope-keyed. `list` is one read of both tables (`listMembers`); every
   * members write invalidates `all`, which also covers `roles`.
   */
  members: {
    all: (tenantKey: string) => ["ll", tenantKey, "members"] as const,
    list: (tenantKey: string) => ["ll", tenantKey, "members", "list"] as const,
    roles: (tenantKey: string) => ["ll", tenantKey, "members", "roles"] as const,
  },
  /**
   * Scenarios (W4-2, SN-D16). A scenario lives in one planning scope (SN-D1), so the LIST key
   * carries the resolved scope id (`scopedParams`); everything keyed by a scenario id is already
   * unique to it. Every scenario write invalidates `all`.
   */
  scenarios: {
    all: (tenantKey: string) => ["ll", tenantKey, "scenarios"] as const,
    list: (tenantKey: string, scopeId: string | null, params?: KeyParams) =>
      ["ll", tenantKey, "scenarios", "list", scopeId ?? "_", ...p(params)] as const,
    detail: (tenantKey: string, scenarioId: string) =>
      ["ll", tenantKey, "scenarios", "detail", scenarioId] as const,
    /** The scenario's OWN changes, by ordinal. */
    overlay: (tenantKey: string, scenarioId: string) =>
      ["ll", tenantKey, "scenarios", "overlay", scenarioId] as const,
    /** The folded effective overlay: ancestors root first, then its own. */
    effective: (tenantKey: string, scenarioId: string) =>
      ["ll", tenantKey, "scenarios", "effective", scenarioId] as const,
    preflight: (tenantKey: string, scenarioId: string) =>
      ["ll", tenantKey, "scenarios", "preflight", scenarioId] as const,
    diff: (tenantKey: string, scenarioId: string, compareWith: string | null) =>
      ["ll", tenantKey, "scenarios", "diff", scenarioId, compareWith ?? "_"] as const,
  },
  uploads: {
    all: (tenantKey: string) => ["ll", tenantKey, "uploads"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "uploads", "list", ...p(params)] as const,
    detail: (tenantKey: string, uploadId: string) =>
      ["ll", tenantKey, "uploads", "detail", uploadId] as const,
    issues: (tenantKey: string, uploadId: string, params?: KeyParams) =>
      ["ll", tenantKey, "uploads", "issues", uploadId, ...p(params)] as const,
  },
  deliveries: {
    all: (tenantKey: string) => ["ll", tenantKey, "deliveries"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "deliveries", "list", ...p(params)] as const,
    detail: (tenantKey: string, deliveryId: string) =>
      ["ll", tenantKey, "deliveries", "detail", deliveryId] as const,
  },
  /**
   * The live half of the data catalogue — one bounded object, no list and no detail. Dataset
   * identity stays in `features/data/dataCatalogue.json` and is never a query key.
   */
  dataCatalogue: {
    all: (tenantKey: string) => ["ll", tenantKey, "dataCatalogue"] as const,
  },
  /** Findings against the data feeds. Derived at query time, so never run-scoped. */
  dataIssues: {
    all: (tenantKey: string) => ["ll", tenantKey, "dataIssues"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "dataIssues", "list", ...p(params)] as const,
    detail: (tenantKey: string, issueId: string) =>
      ["ll", tenantKey, "dataIssues", "detail", issueId] as const,
  },
  /** Findings about one solve. Run-scoped, and computed after the run succeeds. */
  planningIssues: {
    all: (tenantKey: string) => ["ll", tenantKey, "planningIssues"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "planningIssues", "list", ...p(params)] as const,
    detail: (tenantKey: string, issueId: string) =>
      ["ll", tenantKey, "planningIssues", "detail", issueId] as const,
  },
  audit: {
    all: (tenantKey: string) => ["ll", tenantKey, "audit"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "audit", "list", ...p(params)] as const,
  },
  demand: {
    all: (tenantKey: string) => ["ll", tenantKey, "demand"] as const,
    transferRequests: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "demand", "transferRequests", ...p(params)] as const,
    bookedLoads: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "demand", "bookedLoads", ...p(params)] as const,
    reservedCapacity: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "demand", "reservedCapacity", ...p(params)] as const,
  },
  outbound: {
    all: (tenantKey: string) => ["ll", tenantKey, "outbound"] as const,
    channels: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "outbound", "channels", ...p(params)] as const,
    snapshot: (tenantKey: string, snapshotId: string, params?: KeyParams) =>
      ["ll", tenantKey, "outbound", "snapshot", snapshotId, ...p(params)] as const,
  },
  runs: {
    all: (tenantKey: string) => ["ll", tenantKey, "runs"] as const,
    list: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "runs", "list", ...p(params)] as const,
    detail: (tenantKey: string, runId: string) =>
      ["ll", tenantKey, "runs", "detail", runId] as const,
    /**
     * "Which run" — the two server-resolved answers, each its own key because each is its own
     * question. They replaced three client heuristics that read `listPlanningRuns` and depended
     * on the server's default `createdAt desc` ordering as an unwritten contract.
     */
    current: (tenantKey: string, scopeId: string | null = null) =>
      ["ll", tenantKey, "runs", "current", scopeId ?? "_"] as const,
    active: (tenantKey: string, scopeId: string | null = null) =>
      ["ll", tenantKey, "runs", "active", scopeId ?? "_"] as const,
    shipments: (tenantKey: string, runId: string, params?: KeyParams) =>
      ["ll", tenantKey, "runs", "shipments", runId, ...p(params)] as const,
    outbound: (tenantKey: string, runId: string, params?: KeyParams) =>
      ["ll", tenantKey, "runs", "outbound", runId, ...p(params)] as const,
    transferRequests: (tenantKey: string, runId: string, params?: KeyParams) =>
      ["ll", tenantKey, "runs", "transferRequests", runId, ...p(params)] as const,
  },
  reports: {
    all: (tenantKey: string) => ["ll", tenantKey, "reports"] as const,
    laneDetail: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "laneDetail", ...p(params)] as const,
    productDetail: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "productDetail", ...p(params)] as const,
    inboundLaneDetail: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "inboundLaneDetail", ...p(params)] as const,
    laneDaysDrift: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "laneDaysDrift", ...p(params)] as const,
    map: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "map", ...p(params)] as const,
    /** The inbound report's whole-report day aggregation — not a page of the register. */
    inboundLaneDetailByDay: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "inboundLaneDetailByDay", ...p(params)] as const,
    /** One SQL statement against one run's snapshot. Keyed on the statement, so a re-run is a hit. */
    sql: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "reports", "sql", ...p(params)] as const,
  },
  queue: {
    all: (tenantKey: string) => ["ll", tenantKey, "queue"] as const,
    stats: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "queue", "stats", ...p(params)] as const,
  },
  kcc: {
    all: (tenantKey: string) => ["ll", tenantKey, "kcc"] as const,
    salesOrders: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "kcc", "salesOrders", ...p(params)] as const,
    salesForecasts: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "kcc", "salesForecasts", ...p(params)] as const,
    salesPlanning: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "kcc", "salesPlanning", ...p(params)] as const,
    overrides: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "kcc", "overrides", ...p(params)] as const,
    runOutbound: (tenantKey: string, runId: string, params?: KeyParams) =>
      ["ll", tenantKey, "kcc", "runOutbound", runId, ...p(params)] as const,
  },
  analytics: {
    all: (tenantKey: string) => ["ll", tenantKey, "analytics"] as const,
    spend: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "analytics", "spend", ...p(params)] as const,
    savings: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "analytics", "savings", ...p(params)] as const,
    planHealth: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "analytics", "planHealth", ...p(params)] as const,
    explain: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "analytics", "explain", ...p(params)] as const,
    compare: (tenantKey: string, params?: KeyParams) =>
      ["ll", tenantKey, "analytics", "compare", ...p(params)] as const,
  },
} as const;

/**
 * The list key for a register, built from its `key` (the query-key leaf). For `key: 'sites'` this
 * is byte-for-byte `queryKeys.sites.list(tenantKey, params)` — the register machine is generic, so
 * it cannot name a builder, but the literal still lives only here.
 */
export const registerListKey = (tenantKey: string, registerKey: string, params?: KeyParams) =>
  ["ll", tenantKey, registerKey, "list", ...p(params)] as const;

/** The detail key for a register row — `queryKeys.<domain>.detail` in the generic machine's hands. */
export const registerDetailKey = (tenantKey: string, registerKey: string, rowId: string) =>
  ["ll", tenantKey, registerKey, "detail", rowId] as const;

/**
 * The whole-domain prefix a register's write invalidates. The register machine is generic over the
 * domain name, so the literal union `LlQueryKey` cannot describe it; the widening lives HERE, once.
 */
export const registerInvalidationKey = (tenantKey: string, registerKey: string): LlQueryKey =>
  ["ll", tenantKey, registerKey] as unknown as LlQueryKey;

type Builders = typeof queryKeys;
type LeafReturns<T> = T extends (...args: never[]) => infer R
  ? R
  : T extends object
    ? { [K in keyof T]: LeafReturns<T[K]> }[keyof T]
    : never;

/** The union of every key this registry can produce. */
export type LlQueryKey = LeafReturns<Builders>;

/** The domain names, exactly as PROJECT-KNOWLEDGE.md §General rules lists them. */
export type LlDomain = Exclude<keyof Builders, "bootstrap">;

/**
 * The section's four cross-domain effects, named ONCE. Each entry returns the key prefixes to
 * invalidate. Every write additionally invalidates `audit` — see `anyWrite`.
 */
export const crossDomainInvalidations = {
  /** Applying an upload invalidates its resource and `uploads`. */
  applyUpload: (tenantKey: string, resource: "sites" | "products" | "laneCapacities") =>
    [queryKeys[resource].all(tenantKey), queryKeys.uploads.all(tenantKey)] as const,
  /**
   * Creating a run invalidates `runs`, `reports`, `analytics` and `planningIssues`. The findings
   * are the fourth because they default to "the latest succeeded run" server-side: a new run
   * changes the answer without changing the request.
   */
  createRun: (tenantKey: string) =>
    [
      queryKeys.runs.all(tenantKey),
      queryKeys.reports.all(tenantKey),
      queryKeys.analytics.all(tenantKey),
      queryKeys.planningIssues.all(tenantKey),
    ] as const,
  /** Cancelling a run invalidates the same four. */
  cancelRun: (tenantKey: string) =>
    [
      queryKeys.runs.all(tenantKey),
      queryKeys.reports.all(tenantKey),
      queryKeys.analytics.all(tenantKey),
      queryKeys.planningIssues.all(tenantKey),
    ] as const,
  /** A KCC override or revoke invalidates `kcc.*`. */
  kccOverride: (tenantKey: string) => [queryKeys.kcc.all(tenantKey)] as const,
  /** Every write invalidates `audit`. */
  anyWrite: (tenantKey: string) => [queryKeys.audit.all(tenantKey)] as const,
} as const;

/**
 * Clears the whole cache on every identity change. Subscribed once at the provider; there is no
 * caller and no component decides when this happens.
 */
export function clearOnIdentityChange(queryClient: QueryClient): () => void {
  return subscribeSession(() => {
    queryClient.clear();
  });
}
